/**
 * Dashboard quick action buttons (Copy Link, Download QR, Response Notifications, Edit).
 * Mirrors the action row from the My Projects card so users don't have to bounce
 * back to My Projects to grab a link / QR or toggle response notifications.
 *
 * Buttons live in the dashboard header next to the form-select dropdown and are
 * hidden whenever the user has "All Events" selected — these actions are per-form.
 */
(function () {
    'use strict';

    const BUTTON_IDS = [
        'header-copy-link-btn',
        'header-download-qr-btn',
        'header-response-notif-btn',
        'header-edit-form-btn',
        'header-preview-form-btn',
        'header-event-status-btn'
    ];

    function getFirebaseDb() {
        if (window.firebaseDb) return window.firebaseDb;
        if (window.firebase && firebase.firestore) return firebase.firestore();
        return null;
    }

    function getCurrentFormId() {
        const sel = document.getElementById('form-select');
        if (sel && sel.value && sel.value !== 'all') return sel.value;
        const params = new URLSearchParams(window.location.search);
        return params.get('formId') || null;
    }

    function showButtons(visible) {
        BUTTON_IDS.forEach(id => {
            const el = document.getElementById(id);
            if (el) el.style.display = visible ? '' : 'none';
        });
    }

    // The toasts in the dashboard's language (they were English on every page).
    const MESSAGES = {
        en: { copied: 'Link copied to clipboard', copyFailed: 'Could not copy link', qrNotLoaded: 'QR generator not loaded — refresh and try again', downloadFailed: 'Download failed', qrStarted: 'QR code download started', stillLoading: 'Still loading — try again in a moment', planCheckFailed: 'Could not check your plan — try again in a moment' },
        es: { copied: 'Enlace copiado', copyFailed: 'No se pudo copiar el enlace', qrNotLoaded: 'El generador de QR no se cargó: recarga la página e inténtalo de nuevo', downloadFailed: 'La descarga falló', qrStarted: 'Descarga del código QR iniciada', stillLoading: 'Aún cargando: inténtalo en un momento', planCheckFailed: 'No pudimos comprobar tu plan: inténtalo en un momento' },
        fr: { copied: 'Lien copié', copyFailed: 'Impossible de copier le lien', qrNotLoaded: "Le générateur de QR n'a pas chargé : actualisez la page et réessayez", downloadFailed: 'Échec du téléchargement', qrStarted: 'Téléchargement du QR code lancé', stillLoading: 'Chargement en cours : réessayez dans un instant', planCheckFailed: 'Impossible de vérifier votre formule : réessayez dans un instant' },
        de: { copied: 'Link kopiert', copyFailed: 'Link konnte nicht kopiert werden', qrNotLoaded: 'QR-Generator nicht geladen – Seite neu laden und erneut versuchen', downloadFailed: 'Download fehlgeschlagen', qrStarted: 'QR-Code-Download gestartet', stillLoading: 'Wird noch geladen – gleich noch einmal versuchen', planCheckFailed: 'Ihr Tarif konnte nicht geprüft werden – gleich noch einmal versuchen' },
        pt: { copied: 'Link copiado', copyFailed: 'Não foi possível copiar o link', qrNotLoaded: 'O gerador de QR não carregou: recarregue a página e tente de novo', downloadFailed: 'Falha no download', qrStarted: 'Download do QR code iniciado', stillLoading: 'Ainda carregando: tente de novo em instantes', planCheckFailed: 'Não foi possível verificar seu plano: tente de novo em instantes' },
        he: { copied: 'הקישור הועתק', copyFailed: 'לא הצלחנו להעתיק את הקישור', qrNotLoaded: 'מחולל ה-QR לא נטען – רעננו את הדף ונסו שוב', downloadFailed: 'ההורדה נכשלה', qrStarted: 'הורדת קוד ה-QR התחילה', stillLoading: 'עדיין בטעינה – נסו שוב בעוד רגע', planCheckFailed: 'לא הצלחנו לבדוק את החבילה – נסו שוב בעוד רגע' },
        ar: { copied: 'تم نسخ الرابط', copyFailed: 'تعذّر نسخ الرابط', qrNotLoaded: 'لم يتم تحميل مولّد QR، حدّث الصفحة وحاول مجددًا', downloadFailed: 'فشل التنزيل', qrStarted: 'بدأ تنزيل رمز QR', stillLoading: 'لا يزال التحميل جاريًا، حاول بعد لحظة', planCheckFailed: 'تعذّر التحقق من خطتك، حاول بعد لحظة' },
    };
    const msg = (key) => { const lang = String(window.DASHBOARD_LANGUAGE || 'en').slice(0, 2); return (MESSAGES[lang] || MESSAGES.en)[key]; };

    function showToast(message, type) {
        if (window.uiUtils && typeof window.uiUtils.showToast === 'function') {
            window.uiUtils.showToast(message, type || 'info');
            return;
        }
        try { alert(message); } catch (_) { /* ignore */ }
    }

    // Same GA `share` event the builder sends (builder-sharing.js trackShareAction),
    // labelled by where it happened. Until 2026-10-07 only builder shares were
    // counted, so a host who copied the link from the dashboard read as never sharing.
    function trackShare(method) {
        try {
            if (typeof gtag === 'function') gtag('event', 'share', { method: method, event_label: 'dashboard_share' });
        } catch (_) { /* analytics must never break sharing */ }
    }

    async function copyLink() {
        const formId = getCurrentFormId();
        if (!formId) return;
        const url = `${window.location.origin}/${formId}`;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(url);
            } else {
                const ta = document.createElement('textarea');
                ta.value = url;
                ta.style.position = 'fixed';
                ta.style.left = '-9999px';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }
            trackShare('copy_link');
            showToast(msg('copied'), 'success');
        } catch (err) {
            console.error('Copy failed', err);
            showToast(msg('copyFailed'), 'error');
        }
    }

    function downloadQr() {
        const formId = getCurrentFormId();
        if (!formId) return;
        if (!window.qrcode) {
            showToast(msg('qrNotLoaded'), 'error');
            return;
        }
        try {
            const url = `${window.location.origin}/${formId}`;
            const qr = window.qrcode(0, 'M');
            qr.addData(url);
            qr.make();

            const moduleCount = qr.getModuleCount();
            const cellSize = 20;
            const margin = cellSize * 4;
            const canvasSize = moduleCount * cellSize + margin * 2;

            const canvas = document.createElement('canvas');
            canvas.width = canvasSize;
            canvas.height = canvasSize;
            const ctx = canvas.getContext('2d');
            ctx.fillStyle = '#ffffff';
            ctx.fillRect(0, 0, canvasSize, canvasSize);
            ctx.fillStyle = '#000000';
            for (let r = 0; r < moduleCount; r++) {
                for (let c = 0; c < moduleCount; c++) {
                    if (qr.isDark(r, c)) {
                        ctx.fillRect(margin + c * cellSize, margin + r * cellSize, cellSize, cellSize);
                    }
                }
            }

            const titleEl = document.getElementById('dashboard-title');
            const baseTitle = (titleEl?.textContent || formId).trim();
            const safeTitle = baseTitle.replace(/[^a-z0-9_-]+/gi, '-')
                .replace(/^-+|-+$/g, '').toLowerCase() || 'rsvp';

            canvas.toBlob((blob) => {
                if (!blob) {
                    showToast(msg('downloadFailed'), 'error');
                    return;
                }
                const objectUrl = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = objectUrl;
                link.download = `${safeTitle}-qr.png`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
                trackShare('qr_download');
                showToast(msg('qrStarted'), 'success');
            }, 'image/png');
        } catch (err) {
            console.error('QR download error', err);
            showToast(msg('downloadFailed'), 'error');
        }
    }

    /**
     * Response notifications for this event: the shared window
     * (components/notifications-modal.js) - email on/off, address and
     * frequency for the event, push for this device. The same window My
     * Projects and the "Get notified" pill open, so there is one place and
     * one set of choices.
     *
     * The plan question goes to the same gate as the builder's switch. If the
     * gate cannot be reached this REFUSES rather than guessing: "allowed"
     * guessed wrong is how alerts went silent, and "not allowed" guessed wrong
     * shows a paying host a lock.
     */
    async function openNotifications() {
        const formId = getCurrentFormId();
        if (!formId) return;
        if (!window.NotificationsModal || !window.AccessControl || typeof window.AccessControl.checkFeatureAccess !== 'function') {
            showToast(msg('stillLoading'), 'info');
            return;
        }
        let isPaid = false;
        try {
            isPaid = await window.AccessControl.checkFeatureAccess(formId, 'responseNotifications');
        } catch (err) {
            console.error('Notification access check failed', err);
            showToast(msg('planCheckFailed'), 'error');
            return;
        }
        window.NotificationsModal.open(formId, {
            isPaid,
            toast: showToast,
            onUpgrade: () => window.AccessControl.promptForUpgrade
                && window.AccessControl.promptForUpgrade(formId, 'responseNotifications', 'response-notifications'),
            onSaved: (v) => updateNotificationIcon(!!v.enabled)
        });
    }

    function updateNotificationIcon(enabled) {
        const icon = document.getElementById('header-response-notif-icon');
        if (!icon) return;
        icon.className = enabled ? 'bi bi-bell-fill' : 'bi bi-bell';
        const btn = document.getElementById('header-response-notif-btn');
        if (btn) {
            btn.classList.toggle('btn-outline-light', !enabled);
            btn.classList.toggle('btn-warning', enabled);
        }
    }

    async function refreshNotificationIcon() {
        const formId = getCurrentFormId();
        if (!formId) return;
        const db = getFirebaseDb();
        if (!db) return;
        try {
            const snap = await db.collection('rsvp_forms').doc(formId).get();
            if (!snap.exists) return;
            const d = snap.data();
            updateNotificationIcon(!!(d.emailNotifications && d.emailNotifications.enabled));
        } catch (_) { /* non-fatal */ }
    }

    // The builder in the dashboard's language. `focus` opens it at one setting
    // (task #814, e.g. 'event-parts' - see BUILDER_FOCUS_TARGETS in
    // builder-form-loader.js).
    function builderUrl(focus) {
        const formId = getCurrentFormId();
        if (!formId) return null;
        const lang = (window.DASHBOARD_LANGUAGE || 'en').toLowerCase();
        const langMap = {
            en: '/builder', es: '/es/builder', fr: '/fr/builder',
            pt: '/pt/builder', de: '/de/builder', he: '/he/builder', ar: '/ar/builder'
        };
        const base = langMap[lang] || '/builder';
        return `${base}.html?formId=${encodeURIComponent(formId)}` + (focus ? `&focus=${encodeURIComponent(focus)}` : '');
    }

    function updateEditLink() {
        const editBtn = document.getElementById('header-edit-form-btn');
        if (!editBtn) return;
        const url = builderUrl();
        if (!url) {
            editBtn.removeAttribute('href');
            return;
        }
        editBtn.href = url;
    }

    // "Preview RSVP form" (task #885): the event's form as guests see it, in a
    // tab of its own that says it is a preview (nothing sent there is saved),
    // where the host can also see each tagged guest's parts. Injected beside
    // Edit so the seven dashboard.html files need no edit.
    const PREVIEW_TITLE = { en: 'Preview RSVP form', es: 'Vista previa del formulario', fr: 'Aperçu du formulaire', de: 'Formular-Vorschau', pt: 'Prévia do formulário', he: 'תצוגה מקדימה של הטופס', ar: 'معاينة النموذج' };
    function ensurePreviewButton() {
        let btn = document.getElementById('header-preview-form-btn');
        if (btn) return btn;
        const edit = document.getElementById('header-edit-form-btn');
        if (!edit) return null;
        btn = document.createElement('a');
        btn.id = 'header-preview-form-btn';
        btn.className = 'btn btn-sm btn-outline-light dashboard-quick-action';
        btn.target = '_blank';
        btn.rel = 'noopener';
        btn.style.display = 'none';
        const lang = String(window.DASHBOARD_LANGUAGE || 'en').slice(0, 2);
        btn.title = PREVIEW_TITLE[lang] || PREVIEW_TITLE.en;
        btn.setAttribute('aria-label', btn.title);
        btn.innerHTML = '<i class="bi bi-eye"></i>';
        edit.insertAdjacentElement('afterend', btn);
        return btn;
    }
    // Postpone / reschedule / cancel (task #955): event-status-control.js owns
    // the window; this is its button. Shows the status when it is not the
    // usual one, so a postponed event reads as postponed from the header.
    function loadEventStatusControl() {
        if (window.EventStatusControl) return Promise.resolve();
        if (loadEventStatusControl.p) return loadEventStatusControl.p;
        loadEventStatusControl.p = new Promise((resolve, reject) => {
            const sc = document.createElement('script');
            sc.src = '/assets/js/event-status-control.js';
            sc.onload = () => resolve();
            sc.onerror = () => { loadEventStatusControl.p = null; reject(new Error('load')); };
            document.head.appendChild(sc);
        });
        return loadEventStatusControl.p;
    }
    function ensureEventStatusButton() {
        let btn = document.getElementById('header-event-status-btn');
        if (btn) return btn;
        const anchor = document.getElementById('header-preview-form-btn') || document.getElementById('header-edit-form-btn');
        if (!anchor) return null;
        btn = document.createElement('button');
        btn.type = 'button';
        btn.id = 'header-event-status-btn';
        btn.className = 'btn btn-sm btn-outline-light dashboard-quick-action';
        btn.style.display = 'none';
        btn.innerHTML = '<i class="bi bi-calendar2-x" aria-hidden="true"></i><span class="btn-label ms-1"></span>';
        btn.addEventListener('click', () => {
            const formId = getCurrentFormId();
            loadEventStatusControl().then(() => window.EventStatusControl.open(formId))
                .catch(() => showToast(msg('stillLoading'), 'error'));
        });
        anchor.insertAdjacentElement('afterend', btn);
        return btn;
    }
    async function refreshEventStatusButton(formId) {
        const btn = ensureEventStatusButton();
        if (!btn || !formId) return;
        try {
            await loadEventStatusControl();
            const C = window.EventStatusControl;
            btn.title = C.label('button');
            btn.setAttribute('aria-label', btn.title);
            // The words first: the status read below can be slow or refused.
            btn.querySelector('.btn-label').textContent = C.label('button');
            const db = getFirebaseDb();
            const snap = db ? await db.collection('rsvp_forms').doc(formId).get() : null;
            if (getCurrentFormId() !== formId) return;
            const st = (snap && snap.exists && snap.data().eventStatus) || '';
            const label = btn.querySelector('.btn-label');
            // Labelled, so "click Postpone or cancel" finds it (hidden on a phone,
            // like Share access); the status itself while it is not the usual one.
            label.textContent = st === 'postponed' ? C.label('stPostponed').split(':')[0]
                : st === 'cancelled' ? C.label('stCancelled') : C.label('button');
            btn.classList.toggle('btn-warning', st === 'postponed' || st === 'cancelled');
            btn.classList.toggle('btn-outline-light', !(st === 'postponed' || st === 'cancelled'));
        } catch (_) { /* the button still opens the window */ }
    }
    document.addEventListener('qr:event-status-changed', (e) => {
        if (e.detail && e.detail.formId === getCurrentFormId()) refreshEventStatusButton(e.detail.formId);
    });

    function updatePreviewLink(formId) {
        const btn = ensurePreviewButton();
        if (btn) btn.href = `/form.html?id=${encodeURIComponent(formId)}&hostPreview=1`;
    }

    function syncVisibility() {
        const formId = getCurrentFormId();
        ensurePreviewButton();
        ensureEventStatusButton();
        showButtons(!!formId);
        if (formId) {
            updatePreviewLink(formId);
            if (refreshEventStatusButton.last !== formId) {
                refreshEventStatusButton.last = formId;
                refreshEventStatusButton(formId);
            }
            updateEditLink();
            refreshNotificationIcon();
        }
    }

    window.dashboardQuickActions = {
        builderUrl,
        copyLink,
        downloadQr,
        openNotifications,
        refreshNotificationIcon,
        updateNotificationIcon,
        syncVisibility
    };

    function init() {
        const sel = document.getElementById('form-select');
        if (sel) sel.addEventListener('change', syncVisibility);
        // The select is populated async; rerun a couple of times to catch the first
        // load. Cheap and avoids a hard dependency on admin.js init order.
        syncVisibility();
        setTimeout(syncVisibility, 500);
        setTimeout(syncVisibility, 1500);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
